import { Eye } from 'lucide-react';
import React, { useEffect } from 'react';
import { RealEstateCategories } from '../../components/realestate/RealEstateCategories';
import { RealEstateDisplaySettings } from '../../components/realestate/RealEstateDisplaySettings';
import { RealEstateImporter } from '../../components/realestate/RealEstateImporter';
import { RealEstateList } from '../../components/realestate/RealEstateList';
import { RealEstateTags } from '../../components/realestate/RealEstateTags';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useMiniAppModal } from '../../hooks/useMiniAppModal';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useRealEstateSystem } from '../../hooks/useRealEstateSystem';

export default function RealEstateSettings() {
  const { chatbotPublicId } = useChatbot();
  const { openModal } = useMiniAppModal();
  const { miniApps, updateMiniAppLabel, initialized: miniAppsInitialized, initializeMiniApps } = useMiniApps();
  const { loading, error, initialized, initialize } = useRealEstateSystem();

  const realEstateApp = miniApps.find(app => app.id === 'realestate');

  // Initialize both real estate system and mini apps
  useEffect(() => {
    if (chatbotPublicId) {
      if (!initialized) {
        initialize(chatbotPublicId);
      }
      
      if (!miniAppsInitialized) {
        initializeMiniApps(chatbotPublicId);
      }
    }
  }, [chatbotPublicId, initialized, initialize, miniAppsInitialized, initializeMiniApps]);

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!chatbotPublicId) return;
    
    const newLabel = e.target.value;
    updateMiniAppLabel('realestate', newLabel, chatbotPublicId);
  };

  // useRealEstateSystem uses a granular loading state object
  if (loading.initialize && !initialized) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Real estate configuration</h2>
          <p className="text-gray-400">Manage your properties and their display</p>
        </div>
        <button
          onClick={() => openModal('realestate')}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors"
        >
          <Eye className="w-4 h-4" />
          Preview
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Titre de la mini-app */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Mini-app title</h3>
        <input
          type="text"
          value={realEstateApp?.label || ''}
          onChange={handleLabelChange}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Biens immobiliers"
        />
      </div>

      {/* Configuration de l'affichage */}
      <RealEstateDisplaySettings />

      {/* Catégories et Tags */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-6">Categories</h3>
          <RealEstateCategories />
        </div>

        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-6">Tags</h3>
          <RealEstateTags />
        </div>
      </div>

      {/* Import CSV */}
      <RealEstateImporter />

      {/* Liste des biens */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <RealEstateList />
      </div>
    </div>
  );
}